import { useState, useMemo } from "react";
import { MaturityGauge } from "../MaturityGauge";
import { DOMAINS_META } from "@/utils/maturity-colors";
import { SECTION_ICONS } from "@/types/maturity";
import type { ElementType, CyFunCategory } from "@/types/maturity";

type SectionStat = {
  type: ElementType;
  done: number;
  total: number;
};

type PrelimHeaderProps = {
  totalChecked: number;
  totalItems: number;
  score: number;
  sectionStats: SectionStat[];
  categories: CyFunCategory[];
  categoryScores: Record<string, number>;
};

function PrelimHeader({
  totalChecked,
  totalItems,
  score,
  sectionStats,
  categories,
  categoryScores,
}: PrelimHeaderProps) {
  const [summaryOpen, setSummaryOpen] = useState(false);

  const domainStats = useMemo(() => {
    return DOMAINS_META.map((domain) => {
      const domainCats = categories.filter(
        (c) => c.activity_code === domain.id
      );
      const scoredCats = domainCats.filter(
        (c) => categoryScores[c.code] !== undefined
      );
      const scores = scoredCats.map((c) => categoryScores[c.code]);
      const avgScore =
        scores.length > 0
          ? scores.reduce((s, v) => s + v, 0) / scores.length
          : 0;
      const pct =
        avgScore > 1.0 ? Math.round(((avgScore - 1.0) / 1.5) * 100) : 0;
      return { ...domain, score: avgScore, pct };
    });
  }, [categories, categoryScores]);

  return (
    <header
      className="relative overflow-hidden rounded-2xl p-7"
      style={{
        background:
          "linear-gradient(135deg, #0F2440 0%, #1A3A5C 50%, #0D1F36 100%)",
      }}
    >
      <div
        className="absolute -top-10 -right-10 w-60 h-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div className="flex-1 min-w-[240px]">
          <div
            className="text-[10px] font-bold tracking-[2.5px] text-blue-400 mb-2"
            aria-hidden="true"
          >
            NIS2 COMPLIANCE
          </div>
          <h2 className="text-[22px] font-bold leading-tight text-white">
            Chestionar de Evaluare
          </h2>
          <p className="mt-1.5 text-[13px] text-slate-400 leading-relaxed max-w-[420px]">
            Răspundeți la fiecare secțiune și bifați activitățile implementate
            în organizația dvs.
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 shrink-0">
          <MaturityGauge score={score} maxScore={2.5} />
          <span className="text-xs text-slate-400 font-medium">
            Scor preliminar
          </span>
        </div>
      </div>

      <div className="mt-5 relative">
        <div className="flex justify-between text-xs text-slate-400 mb-1.5">
          <span id="prelim-progress-label">Progres total</span>
          <span className="font-semibold text-blue-400">
            {totalChecked} / {totalItems} activități
          </span>
        </div>
        <div
          role="progressbar"
          aria-labelledby="prelim-progress-label"
          aria-valuenow={totalChecked}
          aria-valuemin={0}
          aria-valuemax={totalItems}
          className="h-2 rounded-md overflow-hidden"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <div
            className="h-full rounded-md transition-[width] duration-500 ease-out"
            style={{
              width: `${totalItems ? (totalChecked / totalItems) * 100 : 0}%`,
              background: "linear-gradient(90deg, #3B82F6, #60A5FA)",
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-4">
        {sectionStats.map((stat) => (
          <div key={stat.type} className="flex items-center gap-1.5">
            <span className="text-sm" aria-hidden="true">
              {SECTION_ICONS[stat.type]}
            </span>
            <span className="text-[11px] text-slate-300 font-semibold">
              {stat.type}
            </span>
            <span
              className="text-[10px] font-bold px-1.5 py-px rounded"
              style={{
                background:
                  stat.done === stat.total && stat.total > 0
                    ? "rgba(34,197,94,0.15)"
                    : "rgba(255,255,255,0.08)",
                color:
                  stat.done === stat.total && stat.total > 0
                    ? "#4ADE80"
                    : "#94A3B8",
              }}
            >
              {stat.done}/{stat.total}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={() => setSummaryOpen(!summaryOpen)}
        aria-expanded={summaryOpen}
        aria-controls="summary-panel"
        className="flex items-center gap-2 mt-4 border-none text-slate-400 cursor-pointer text-xs font-semibold py-1 px-0 bg-transparent"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
          className="transition-transform duration-200"
          style={{
            transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {summaryOpen ? "Ascunde sumar categorii" : "Afișează sumar categorii"}
      </button>

      {summaryOpen && (
        <div id="summary-panel" className="mt-3.5">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2.5">
            {domainStats.map((domain) => (
              <div
                key={domain.id}
                className="bg-white rounded-[10px] py-3.5 px-4"
                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
              >
                <div className="flex justify-between items-center mb-2.5">
                  <span
                    className="text-[11px] font-bold tracking-[0.5px]"
                    style={{ color: domain.color }}
                  >
                    {domain.name}
                  </span>
                  <span className="text-[13px] font-bold text-slate-800">
                    {domain.pct}%
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-label={`${domain.name} progres`}
                  aria-valuenow={domain.pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-[5px] bg-slate-100 rounded-sm overflow-hidden"
                >
                  <div
                    className="h-full rounded-sm transition-[width] duration-300 ease-out"
                    style={{
                      width: `${domain.pct}%`,
                      background: domain.color,
                    }}
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  {domain.pct}% completat
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export default PrelimHeader;
